import { useState, useEffect, useCallback } from 'react';
import { fetchUserAttributes } from '@aws-amplify/auth';
import { getUserSettings, getOrgConfig, getInitData } from '../utils/api';
import { getOrganizations } from '../utils/organizationApi';
import { bootIntercom } from '../utils/intercom';
import { isEmulating, getEmulatedUser } from '../utils/emulation';
import { clearRequestedOrg, requestedOrgId } from '../utils/deepLink';

const USER_SETTINGS_KEY = 'userSettings';

export const useUserSettings = () => {
  const [userSettings, setUserSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Start as false since we don't auto-fetch
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);

  const _updateCurrentOrgContext = useCallback((org, allAccessibleOrgs, settings = null) => {
    if (!org) {
      console.warn('Attempted to set current organization to null/undefined');
      setCurrentOrganization(null);
      localStorage.removeItem('currentOrgCode');
      localStorage.removeItem('currentOrgId');
      localStorage.removeItem('currentOrgWorkspaceId');
      return;
    }

    const accessibleOrg = allAccessibleOrgs.find(aOrg => aOrg.code === org.code);
    if (!accessibleOrg) {
       console.warn(`Organization with code ${org.code} is not accessible to the current user.`);
       if (allAccessibleOrgs.length > 0) {
          _updateCurrentOrgContext(allAccessibleOrgs[0], allAccessibleOrgs, settings);
       } else {
          setCurrentOrganization(null);
          localStorage.removeItem('currentOrgCode');
          localStorage.removeItem('currentOrgId');
          localStorage.removeItem('currentOrgWorkspaceId');
       }
       return;
    }


    setCurrentOrganization(accessibleOrg);
    localStorage.setItem('currentOrgCode', accessibleOrg.code);
    if (accessibleOrg.id !== undefined && accessibleOrg.id !== null) {
       localStorage.setItem('currentOrgId', accessibleOrg.id);
    } else {
       console.warn('Selected organization object is missing an ID:', accessibleOrg);
       localStorage.removeItem('currentOrgId');
    }
    if (accessibleOrg.workspaceId) {
      localStorage.setItem('currentOrgWorkspaceId', accessibleOrg.workspaceId);
    } else {
       console.warn('Selected organization object is missing workspaceId:', accessibleOrg);
      localStorage.removeItem('currentOrgWorkspaceId');
    }

    // Update Intercom with the user's role for this organization via backend API
    // Get settings from parameter or from localStorage
    const userSettingsData = settings || JSON.parse(localStorage.getItem('userSettings') || '{}');

    if (!isEmulating() && accessibleOrg.code && userSettingsData.orgAccess?.[accessibleOrg.code]) {
      const userRole = userSettingsData.orgAccess[accessibleOrg.code].role || 'user';
      const userEmail = userSettingsData.email;
      const userName = userSettingsData.name || userSettingsData.given_name;

      console.log('[Intercom Update] Sending to backend:', {
        organization_role: userRole,
        current_organization: accessibleOrg.code
      });

      // Fire and forget - don't block on this
      import('../utils/api').then(({ updateIntercomAttributes }) => {
        updateIntercomAttributes(userEmail, userEmail, userName, userRole, accessibleOrg.code)
          .then(result => {
            if (result.success) {
              console.log('[Intercom Update] Backend update successful');
            } else {
              console.warn('[Intercom Update] Backend update returned:', result);
            }
          })
          .catch(err => console.error('[Intercom Update] Backend update failed:', err));
      });
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    let loadedSettings = null;
    let allOrgsFromApi = [];
    let orgConfig = [];
    let accessibleOrgs = [];

    try {
      // Check if we're in emulation mode
      const emulating = isEmulating();
      const emulatedUser = emulating ? getEmulatedUser() : null;

      const attributes = await fetchUserAttributes();
      const userEmail = attributes?.email;
      if (!userEmail) throw new Error('User email not found');

      // Fetch initialization data first, then initialize Intercom with complete user info including role
      if (emulating && emulatedUser) {
        // When emulating, use the emulated user's settings instead of fetching admin's
        console.log('[useUserSettings] Emulation mode - using emulated user settings:', emulatedUser.email);
        loadedSettings = emulatedUser;

        // Still need to fetch org config to build organization objects
        const initData = await getInitData();
        allOrgsFromApi = initData.organizations;
        orgConfig = initData.orgConfig;
      } else {
        // Normal mode - fetch all initialization data in a single optimized backend call
        const initData = await getInitData();

        loadedSettings = initData.userSettings;
        allOrgsFromApi = initData.organizations;
        orgConfig = initData.orgConfig;
      }

      if (!loadedSettings) throw new Error('Failed to load user settings');
      
      // Ensure settings structure (isAdmin, orgAccess properties)
      loadedSettings.isAdmin = loadedSettings.isAdmin || 0;
      // Ensure orgAccess exists and log if it doesn't
      if (loadedSettings.orgAccess) {

        Object.keys(loadedSettings.orgAccess).forEach(orgCode => {
           const orgAccessData = loadedSettings.orgAccess[orgCode];
           loadedSettings.orgAccess[orgCode] = {
             reportIds: orgAccessData.reportIds || {},
             dashboardIds: orgAccessData.dashboardIds || [],
             locationIds: orgAccessData.locationIds || [],
             departmentIds: orgAccessData.departmentIds || [-1], // Default to -1 for "all access"
             chartOfAccountDepartmentIds: orgAccessData.chartOfAccountDepartmentIds || [-1], // Default to -1 for "all access"
             dashboards: orgAccessData.dashboards || [],
             reports: orgAccessData.reports || [],
             // PBI report grants mapped to native report groups (server-stamped
             // on load). Array = only these; [] = none; null = unrestricted.
             nativeReports: Array.isArray(orgAccessData.nativeReports) ? orgAccessData.nativeReports : null,
             role: orgAccessData.role || ''
           };
        });
      } else {
        console.warn('[useUserSettings] loadedSettings.orgAccess is missing or null.');
        loadedSettings.orgAccess = {}; // Ensure orgAccess exists
      }
      
      setUserSettings(loadedSettings);
      localStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(loadedSettings));
      
      // 3. Process organizations: Merge data from all sources for accessible orgs
      if (loadedSettings.orgAccess && allOrgsFromApi.length > 0) {
        const accessibleOrgCodes = Object.keys(loadedSettings.orgAccess);

        // BFF already returns complete organizations with workspaceId and access merged
        // Just filter by accessible org codes - no need to re-merge
        accessibleOrgs = allOrgsFromApi
          .map(org => {

             return org;
          })
          .filter(org => {
            const isAccessible = accessibleOrgCodes.includes(org.code);
            return isAccessible;
          });
        

        setOrganizations(accessibleOrgs); // Set the list of fully formed accessible org objects
      } else {
        console.warn('[useUserSettings] No orgAccess in settings or no orgs fetched from API. Setting organizations to empty.');
        setOrganizations([]); // No accessible orgs
        accessibleOrgs = [];
      }

      // 4. Determine and set the current organization
      let initialOrg = null;
      if (accessibleOrgs.length > 0) {
        const savedOrgCode = localStorage.getItem('currentOrgCode');
        // A deep link (?org=<id>, e.g. from a report email) wins over the
        // last-used org, as long as the user can access it.
        const linkedOrgId = requestedOrgId();
        if (linkedOrgId) {
           initialOrg = accessibleOrgs.find(org => String(org.id) === String(linkedOrgId));
           // Applied or refused, the link has had its say; left in the URL it
           // would switch the user back on every reload.
           clearRequestedOrg();
        }

        if (!initialOrg && savedOrgCode) {
           initialOrg = accessibleOrgs.find(org => org.code === savedOrgCode);

        }
        // If no saved code or saved code org not found/accessible, use the first one
        if (!initialOrg) {

          initialOrg = accessibleOrgs[0];
        }
      }

      // 5. Initialize Intercom with complete user info including role (skip when emulating)
      if (!emulating) {
        const initialRole = (initialOrg && loadedSettings.orgAccess?.[initialOrg.code]?.role) || 'user';
        console.log('[Intercom Init] Initializing with role:', {
          email: userEmail,
          orgCode: initialOrg?.code,
          role: initialRole
        });

        try {
          const { getIntercomJWT } = await import('../utils/api');
          // Pass role and org to backend - it will update Intercom via REST API
          const jwtResponse = await getIntercomJWT(
            userEmail,
            userEmail,
            attributes.given_name,
            initialRole,
            initialOrg?.code || ''
          );

          if (!jwtResponse.success || !jwtResponse.jwt) {
            console.warn('[useUserSettings] Failed to get Intercom JWT, initializing without authentication');
          }

          // bootIntercom owns app_id and hide_default_launcher: the messenger has
          // no launcher of its own, because support is an item in the Lodgeic
          // assistant dock. Passing settings inline here is how that flag drifts.
          bootIntercom({
            userId: userEmail,
            name: attributes.given_name,
            email: userEmail,
            jwt: jwtResponse.success ? jwtResponse.jwt : null,
          });
          console.log('[Intercom Init] Successfully initialized');
        } catch (intercomError) {
          console.error('[useUserSettings] Intercom initialization error:', intercomError);
          // Initialize without JWT as fallback
          bootIntercom({
            userId: userEmail,
            name: attributes.given_name,
            email: userEmail,
          });
        }
      }

      // 6. Update current organization context
      if (initialOrg) {
        _updateCurrentOrgContext(initialOrg, accessibleOrgs, loadedSettings);
      } else {
        console.warn('[useUserSettings] No accessible organizations found. Clearing current organization.');
        // No accessible organizations, clear current org
        _updateCurrentOrgContext(null, []);
      }

    } catch (err) {
      console.error('Error loading user settings or organization data:', err);
      // Ensure state is cleared on error
      setError(err.message || 'Failed to load data');
      // Clear potentially inconsistent state on error
      setUserSettings(null);
      setOrganizations([]);
      setCurrentOrganization(null);
      localStorage.removeItem(USER_SETTINGS_KEY);
      localStorage.removeItem('currentOrgCode');
      localStorage.removeItem('currentOrgId');
      localStorage.removeItem('currentOrgWorkspaceId');
    } finally {
      setIsLoading(false);
    }
    // Callers that must act on the doc before React re-renders (Login applies
    // the appearance preferences before navigating into the app).
    return loadedSettings;
  }, [_updateCurrentOrgContext]);

  // Don't auto-fetch on mount - this causes race conditions when the user
  // isn't authenticated yet (e.g., on the login page). Instead, we rely on:
  // 1. Login.js calling fetchSettings() after successful authentication
  // 2. ProtectedRoute calling fetchSettings() after verifying auth
  // This prevents the "UserUnAuthenticatedException" error on the login page.

  const setCurrentOrg = useCallback((orgCode) => {
    if (!orgCode) {
      console.warn('setCurrentOrg called with null or empty orgCode');
      return;
    }
    const orgToSet = organizations.find(org => org.code === orgCode);
    if (orgToSet) {
      _updateCurrentOrgContext(orgToSet, organizations);
    } else {
      console.error(`Attempted to switch to non-accessible or non-existent organization code: ${orgCode}`);
    }
  }, [organizations, _updateCurrentOrgContext]);
  
  const updateSettings = async (newSettings) => {
    setIsLoading(true);
    setError(null);
    try {
       const updatedSettings = { ...userSettings, ...newSettings };
       updatedSettings.isAdmin = updatedSettings.isAdmin || 0;
       if (updatedSettings.orgAccess) {
          Object.keys(updatedSettings.orgAccess).forEach(orgCode => {
             const orgAccessData = updatedSettings.orgAccess[orgCode];
             updatedSettings.orgAccess[orgCode] = {
               reportIds: orgAccessData.reportIds || {},
               dashboardIds: orgAccessData.dashboardIds || [],
               locationIds: orgAccessData.locationIds || [],
               departmentIds: orgAccessData.departmentIds || [-1], // Default to -1 for "all access"
               chartOfAccountDepartmentIds: orgAccessData.chartOfAccountDepartmentIds || [-1], // Default to -1 for "all access"
               dashboards: orgAccessData.dashboards || [],
               reports: orgAccessData.reports || [],
               // PBI report grants mapped to native report groups (server-stamped
               // on load). Array = only these; [] = none; null = unrestricted.
               nativeReports: Array.isArray(orgAccessData.nativeReports) ? orgAccessData.nativeReports : null,
               role: orgAccessData.role || ''
             };
          });
       }

       setUserSettings(updatedSettings);
       localStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(updatedSettings));
       
       if ('orgAccess' in newSettings) {

          await loadAllData();
       }
       
       return updatedSettings;
    } catch (err) {
       console.error('Error updating user settings:', err);
       setError(err.message || 'Failed to update user settings');
       return null;
    } finally {
       setIsLoading(false);
    }
  };

  return {
    userSettings,
    organizations,
    currentOrganization,
    isLoading,
    error,
    fetchSettings: loadAllData,
    updateSettings, 
    setCurrentOrg
  };
}; 